// alternative-dashboard/InsightsCFOCopilot.tsx
import { useSelector } from "react-redux";
import type { DashboardLayoutMenuDetail } from "@/app/types/alterNativeDashboardLayout";
import type { RootState } from "@/app/redux/store";
import DashboardWidgetRenderer from "./DashboardWidgetRenderer";
import { INSIGHTS_WIDGET_REGISTRY } from "./dashboardWidgetRegistryConfig/insightsWidgetRegistry.config";
import {
  INSIGHTS_WIDGET_LAYOUT,
  type InsightsWidgetSize,
} from "./dashboardWidgetRegistryConfig/insightsWidgetLayout.config";
import SubmitLoading from "@/app/components/webloading/errorHandling/SubmitLoading";
import { useDashboardLayoutData } from "./types/useDashboardLayoutData";
import "./alternative-components/CFOCopilot/styles/CustomeChart.scss";

const DAILY_BRIEF_MENU_ID = 208;

const SIZE_TO_COLUMNS: Record<InsightsWidgetSize, number> = {
  full: 1,
  half: 2,
  third: 3,
};

function InsightsCFOCopilot() {
  const { menuDetail, loading } = useDashboardLayoutData();

  const tenantId = useSelector(
    (state: RootState) => state.userData?.data?.tenantId,
  );

  if (!tenantId || loading) {
    return (
      <div className="gx-bene-form-container-loading-overlay">
        <SubmitLoading />
      </div>
    );
  }

  const menuMap = new Map<number, DashboardLayoutMenuDetail>(
    menuDetail
      .filter((m: DashboardLayoutMenuDetail) => m.recordState === "A")
      .map((menu: DashboardLayoutMenuDetail) => [menu.menuId, menu]),
  );

  const rows: {
    type: InsightsWidgetSize;
    widgets: DashboardLayoutMenuDetail[];
  }[] = [];

  for (let i = 0; i < INSIGHTS_WIDGET_LAYOUT.length; ) {
    const current = INSIGHTS_WIDGET_LAYOUT[i];
    const columns = SIZE_TO_COLUMNS[current.size];

    const groupEntries = INSIGHTS_WIDGET_LAYOUT.slice(i, i + columns).filter(
      (entry) => entry.size === current.size,
    );

    const widgets = groupEntries
      .map((entry) => menuMap.get(entry.menuId))
      .filter((menu): menu is DashboardLayoutMenuDetail => Boolean(menu));

    rows.push({ type: current.size, widgets });
    i += groupEntries.length;
  }

  return (
    <div className="gx-insights-cfo-copilot">
      {rows.map((row, index) => {
        if (row.widgets.length === 0) return null;

        return (
          <div className="gx-cfo-copilot-row" key={index}>
            {row.widgets.map((menu) => {
              const isBare = menu.menuId === DAILY_BRIEF_MENU_ID;

              return (
                <div
                  className={`gx-cfo-copilot-panel gx-cfo-copilot-panel--${row.type}${
                    isBare ? " gx-cfo-copilot-panel--bare" : ""
                  }`}
                  key={menu.menuId}
                >
                  <DashboardWidgetRenderer
                    menuDetail={[menu]}
                    registry={INSIGHTS_WIDGET_REGISTRY}
                  />
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export default InsightsCFOCopilot;
